import type { FC } from "react";
import { useMemo } from "react";

import { useStoreLocatorContext } from "@locators/components/shared/hooks";
import { OPEN_NOW_CATEGORY } from "@locators/components/shared/utils";
import classNames from "classnames";

import { Checkbox } from "@jti/core-components/components/Checkbox";
import { DrawerRoot } from "@jti/ui/components/Drawer";
import { DropdownSelect } from "@jti/ui/components/Inputs";
import { useDeviceBreakpoint } from "@jti/ui/hooks/useDeviceBreakpoint";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { StoreLocatorFiltersDrawer, StoreLocatorFiltersMobile } from "./components/";

import styles from "./StoreLocatorFiltersV2.module.scss";

const getSelectedFiltersCount = (selectedFilters: Record<string, unknown>, categoryKey: string) => {
  return Object.keys(selectedFilters).reduce((counter, filter) => {
    if (filter.includes(categoryKey)) {
      return counter + 1;
    }
    return counter;
  }, 0);
};

export const StoreLocatorFiltersV2: FC = () => {
  const { __ } = useTranslations();
  const { breakpoint } = useDeviceBreakpoint();
  const {
    newFilters,
    newFiltersNumber,
    filtersEnabled,
    isOpenNowFilterEnabled,
    activeFilters,
    selectedFilters,
    applyFilters,
    checkFiltersStatus,
    changeFilters,
    handleDropdownRestart,
    handleDropdownSelect,
    handleApplyOpenNowCheckbox,
  } = useStoreLocatorContext();

  const isMobileOrTablet = useMemo(() => breakpoint !== "desktop", [breakpoint]);

  if (!filtersEnabled) {
    return null;
  }

  return (
    <DrawerRoot>
      {isMobileOrTablet ? (
        <StoreLocatorFiltersMobile
          applyFilters={applyFilters}
          changeFilters={changeFilters}
          checkFiltersStatus={checkFiltersStatus}
        />
      ) : (
        <div className={styles.filtersWrapper} data-testid="StoreLocatorFiltersV2">
          {isOpenNowFilterEnabled && (
            <Checkbox
              label={__("store_locator.popup.shop_status.open")}
              checked={checkFiltersStatus(OPEN_NOW_CATEGORY, OPEN_NOW_CATEGORY)}
              onCheckedChange={handleApplyOpenNowCheckbox}
            />
          )}
          <div className={classNames(styles.dropdownSelectContainer)}>
            {newFilters?.slice(0, 2).map((filterCategory, i) => {
              return (
                <DropdownSelect
                  dataTestId={`storeLocatorFiltersDropdown-${i}`}
                  containerClassName={styles.dropdownSelect}
                  options={filterCategory.filters}
                  label={filterCategory.name}
                  onChange={handleDropdownSelect.bind(this, filterCategory.key)}
                  onRestart={handleDropdownRestart.bind(this, filterCategory.key)}
                  value={activeFilters[filterCategory.key] || []}
                  onApply={applyFilters}
                  key={filterCategory.key}
                  applyButtonText={__("storelocator.button.apply")}
                  restartButtonText={__("storelocator.button.cancel")}
                  selectedItemCount={getSelectedFiltersCount(selectedFilters, filterCategory.key)}
                />
              );
            })}
          </div>
          {newFiltersNumber > 2 && (
            <StoreLocatorFiltersDrawer
              applyFilters={applyFilters}
              changeFilters={changeFilters}
              checkFiltersStatus={checkFiltersStatus}
            />
          )}
        </div>
      )}
    </DrawerRoot>
  );
};
